import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import Digital from './clarity.png';
import RealTime from './betteryou.png';
import Interactive from './destress.png';
import Dashboard from './calmwhite.png';
import Guidelines from './calm.png';
import LongTerm from './horizon.png';

const Section = forwardRef((props, ref) => {
  // (1) List of all feature “slides”
  const features = [
    {
      title: 'Digital Biometrics',
      description:
        'Seamlessly capture digital biometrics while interacting with your devices, providing real-time insights into your cognitive state and performance levels.',
      img: Digital,
    },
    {
      title: 'Real-Time Feedback',
      description:
        'Get instantaneous insights to optimize your focus and health in the moment, helping you make better decisions about your work patterns.',
      img: RealTime,
    },
    {
      title: 'Interactive Guidance',
      description:
        'Receive personalized guidance to enhance your productivity patterns, with AI-driven recommendations that adapt to your unique work style.',
      img: Interactive,
    },
    {
      title: 'User Dashboard',
      description:
        'View your performance metrics and wellness data through an intuitive interface that makes complex data easy to understand and act upon.',
      img: Dashboard,
    },
    {
      title: 'Health Guidelines',
      description:
        'Stay compliant with occupational health standards for workplace wellness, ensuring your work environment promotes long-term health.',
      img: Guidelines,
    },
    {
      title: 'Long-Term Benefits',
      description:
        'Track your progress and see improvement over time with historical data analysis that helps you build better work habits.',
      img: LongTerm,
    },
  ];

  const [currentFeature, setCurrentFeature] = useState(0);
  const sectionRef = useRef(null);

  // Expose ref from forwardRef
  const assignRef = (node) => {
    sectionRef.current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  };

  // (2) Auto‐advance every 5 seconds:
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % features.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [features.length]);

  return (
    <section ref={assignRef} className="features-section">
      <div className="features-container">
        <h2 className="features-title">WhiteMatter Features</h2>

        <div className="features-content">
          {/* IMAGE SIDE */}
          <div className="features-image-container">
            <AnimatePresence mode="wait">
              <motion.div
                key={features[currentFeature].title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="feature-image-wrapper"
              >
                <motion.img
                  src={features[currentFeature].img}
                  alt={features[currentFeature].title}
                  className="feature-image"
                  initial={{ scale: 0.95 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0.95 }}
                  transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* TEXT SIDE */}
          <div className="features-text-container">
            <AnimatePresence mode="wait">
              <motion.div
                key={features[currentFeature].title + '-text'}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="feature-text-content"
              >
                <h3 className="feature-title">{features[currentFeature].title}</h3>
                <p className="feature-description">
                  {features[currentFeature].description}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* NAVIGATION DOTS (manual controls) */}
        <div className="features-navigation">
          {features.map((_, index) => (
            <button
              key={index}
              className={`nav-dot ${currentFeature === index ? 'active' : ''}`}
              onClick={() => setCurrentFeature(index)}
              aria-label={`View feature ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
});

export default Section;
